"use client";
import {useState, useEffect, useRef} from "react";
import {m, useScroll} from "framer-motion";
import {GitBranch, Tag, Terminal, Trash2, Plus} from "lucide-react";
import {useRouter} from "next/navigation";
import {type TimelineEntry} from "@/types/types";

interface TimelineSectionProps {
  initialTimeline?: TimelineEntry[];
}

export default function TimelineSection({ initialTimeline = [] }: TimelineSectionProps) {
  const [entries, setEntries] = useState<TimelineEntry[]>(initialTimeline);
  const isDev = process.env.NODE_ENV === "development";
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll Progress Tracking for Timeline vertical line
  const {scrollYProgress} = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  useEffect(() => {
    setEntries(initialTimeline);
  }, [initialTimeline]);

  // Fetch combined local + public entries in development mode only
  useEffect(() => {
    if (isDev) {
      const fetchTimeline = async () => {
        try {
          const res = await fetch("/api/timeline");
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data)) {
              setEntries(data);
            }
          }
        } catch (e) {
          console.error("Failed to load local timeline entries:", e);
        }
      };
      
      fetchTimeline();
    }
  }, [isDev]);

  const handlePromote = async (id: string, title: string) => {
    if (!confirm(`Promote milestone "${title}" to production?`)) {
      return;
    }
    try {
      const res = await fetch("/api/timeline", {
        method: "PATCH",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({id}),
      });
      if (res.ok) {
        // Refresh local data
        const updated = await fetch("/api/timeline").then((r) => r.json());
        if (Array.isArray(updated)) setEntries(updated);
        router.refresh();
      } else {
        const data = await res.json();
        alert(`Error: ${data.error}`);
      }
    } catch {
      alert("Failed to promote entry.");
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Delete milestone "${title}"?`)) {
      return;
    }
    try {
      const res = await fetch(`/api/timeline?id=${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        // Refresh local data
        const updated = await fetch("/api/timeline").then((r) => r.json());
        if (Array.isArray(updated)) setEntries(updated);
        router.refresh();
      } else {
        const data = await res.json();
        alert(`Error: ${data.error}`);
      }
    } catch {
      alert("Failed to delete entry.");
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "creation":
        return <GitBranch className="w-4 h-4 text-yellow-400" />;
      case "release":
        return <Tag className="w-4 h-4 text-yellow-400" />;
      case "milestone":
      default:
        return <Terminal className="w-4 h-4 text-yellow-400" />;
    }
  };

  return (
    <section ref={containerRef} id="timeline" className="py-24 border-t-2 border-zinc-800 scroll-mt-20 font-mono">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14">
          <h2 className="text-4xl md:text-5xl font-black uppercase text-zinc-50 tracking-tighter">
            SYSTEM_TIMELINE
          </h2>
          <div className="w-16 h-1.5 bg-yellow-400 mt-3" />
          <p className="mt-4 text-zinc-400 text-sm max-w-lg">
            Chronological audit log of repository creations, version releases, and system milestones.
          </p>
        </div>

        {entries.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-800 p-12 text-center">
            <p className="text-sm text-zinc-500">NO_TIMELINE_RECORDS_AVAILABLE</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical timeline line background */}
            <div className="absolute top-0 bottom-0 left-[21px] md:left-1/2 md:-translate-x-1/2 w-1 bg-zinc-800" />
            
            {/* Scroll-Linked Vertical timeline line overlay */}
            <m.div
              style={{ scaleY: scrollYProgress, originY: 0 }}
              className="absolute top-0 bottom-0 left-[21px] md:left-1/2 md:-translate-x-1/2 w-1 bg-yellow-400"
            />

            <div className="space-y-12">
              {entries.map((entry, idx) => {
                const isEven = idx % 2 === 0;
                const dateFormatted = new Date(entry.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }).toUpperCase();

                return (
                  <div
                    key={entry.id}
                    className={`flex flex-col md:flex-row items-start md:items-center relative ${
                      isEven ? "md:flex-row-reverse" : ""
                    }`}
                  >
                    {/* Event Circle Anchor */}
                    <div className="absolute left-3 md:left-1/2 md:-translate-x-1/2 z-10 flex items-center justify-center w-[18px] h-[18px] bg-zinc-950 border-2 border-zinc-800 rounded-none">
                      <div className="w-2 h-2 bg-yellow-400 rounded-none" />
                    </div>

                    {/* Timeline Card */}
                    <div className="w-full md:w-[calc(50%-30px)] pl-10 md:pl-0">
                      <div className="border-2 border-zinc-800 bg-zinc-900/40 p-5 rounded-none relative">
                        {/* Dev management controls on card header */}
                        {isDev && (
                          <div className="absolute top-3 right-3 flex gap-1.5 z-20">
                            {entry.private && (
                              <button
                                onClick={() => handlePromote(entry.id, entry.title)}
                                className="p-1 bg-emerald-500 text-zinc-950 border border-zinc-950 font-bold hover:bg-emerald-400 transition-none cursor-pointer"
                                title="Publish to Production"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(entry.id, entry.title)}
                              className="p-1 bg-red-600 text-zinc-50 border border-zinc-950 font-bold hover:bg-red-500 transition-none cursor-pointer"
                              title="Delete Milestone"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}

                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center justify-center w-7 h-7 bg-zinc-950 border border-zinc-800">
                            {getIcon(entry.type)}
                          </div>
                          <span className="text-[10px] font-bold text-yellow-400">
                            {dateFormatted}
                          </span>
                          {entry.private && (
                            <span className="px-1.5 py-0.5 text-[8px] bg-zinc-800 text-zinc-400 border border-zinc-700 font-bold">
                              DRAFT
                            </span>
                          )}
                        </div>

                        <h3 className="text-sm font-bold uppercase text-zinc-100 tracking-tighter mb-2">
                          {entry.title}
                        </h3>
                        
                        <p className="text-xs text-zinc-400 leading-relaxed">
                          {entry.description}
                        </p>

                        {entry.githubUrl && (
                          <a
                            href={entry.githubUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-block mt-3 text-[10px] text-yellow-400 hover:underline"
                          >
                            &gt;&gt; INSPECT_REMOTE
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {/* Empty spacer for grid alignment */}
                    <div className="hidden md:block w-[calc(50%-30px)]" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
